import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  type GridProps,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import {
  Card,
  Link,
  ModalConfirm,
  NumberInput,
  RemoveButton,
  Select,
  TextField,
} from "@components/ui-kit";
import { TagSelect } from "@components/ui-kit/Select/tag";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Trans, t } from "@lingui/macro";
import {
  OutgoingInvoiceTypeEnum,
  useGetTotalInWordsQuery,
} from "@src/__generated__/urql-graphql";
import { ExchangeRateSourceSelect } from "@src/components/modules/invoices/Create/components/ExchangeRateSourceSelect";
import type { TInvoiceItemForm } from "@src/components/modules/invoices/Create/form/Form.types";
import { Icon } from "@src/components/ui-kit/Icon";
import { cannot } from "@src/utils/components/permissions";
import { currency, formatDate } from "@src/utils/formatters";
import {
  fieldNumberModelToInputProps,
  fieldToInputProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { toTagOptions } from "@src/utils/map-to-options/tags";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import type { LinkProps } from "next/link";
import { useEffect, useMemo } from "react";
import { useDebounce } from "react-use";
import { useInitInvoiceItem } from "../../../hooks";
import { InvoiceTypeEnum, useInvoiceStore } from "../../../store";
import { ExchangeRateInput } from "../../ExchangeRateInput";

const LITHUANIA_COUNTRY_CODE = "LT";

function getInvoiceItemsTemplateColumn(
  showQuantity: boolean,
): GridProps["templateColumns"] {
  return {
    base: `75px 1fr 1fr ${showQuantity ? "130px 50px" : ""} 110px 75px 35px`,
    "1.5xl": `85px 1fr 1fr ${showQuantity ? "145px 60px" : ""} 125px 85px 35px`,
  };
}

export const InvoiceItemsCard = observer(function InvoiceItemsCard() {
  const { workspaceStore, tagStore } = useStore();
  const store = useInvoiceStore();
  const [parent] = useAutoAnimate<HTMLDivElement>();
  const confirmAutocompleteInvoiceItemsModal = useDisclosure();
  const totalText = store.showVat ? t`Total with VAT` : t`Total`;
  const lastInvoice = store.selectedProjects[0]?.lastInvoice;
  const templateColumns = getInvoiceItemsTemplateColumn(
    store.showQuantity.value,
  );

  const lithuanianWorkspace =
    workspaceStore.settings?.country.code.toUpperCase() ===
    LITHUANIA_COUNTRY_CODE;
  const lastInvoicePathname = useMemo(() => {
    let pathname: LinkProps["href"];

    switch (lastInvoice?.outgoingInvoice.type) {
      case OutgoingInvoiceTypeEnum.Invoice:
        pathname = "/invoices/outgoing-invoices/edit";
        break;
      case OutgoingInvoiceTypeEnum.CreditNote:
        pathname = "/invoices/for-invoicing/edit";
        break;
      case OutgoingInvoiceTypeEnum.ProformaPayment:
        pathname = "/invoices/proforma-invoices/edit";
        break;
      default:
        pathname = "/404";
        break;
    }

    return pathname;
  }, [lastInvoice]);

  useInitInvoiceItem();

  const [
    { fetching: fetchingTotalInWords, data: totalInWords },
    fetchTotalInWords,
  ] = useGetTotalInWordsQuery({
    pause: true,
    variables: {
      amount: Math.round(store.sumInvoiceItemsTotal),
      language:
        store.selectedBillingEntity?.country.code ??
        workspaceStore.settings?.country.code ??
        "EN",
    },
  });

  useDebounce(
    () => {
      if (lithuanianWorkspace) {
        fetchTotalInWords();
      }
    },
    300,
    [store.sumInvoiceItemsTotal],
  );

  useEffect(() => {
    store.form.total_in_words.onChange(totalInWords?.getTotalInWords);
  }, [totalInWords?.getTotalInWords]);

  const createEmptyItem = () => {
    store.form.addInvoiceItem({
      description: "",
      amount_in_currency: new NumberModel(
        0,
        store.selectedCurrency.denomination,
      ),
      quantity: "",
      accounting_account_id:
        store.form.invoice_items.$.at(-1)?.$.accounting_account_id.$ ?? "",
      unit_price: "0",
      cost_center_id: "",
      tag_ids: [],
      vat_percent_informative: store.form.vat_percent_informative.$ ?? "0",
    });
  };

  const onGenerateItems = async () => {
    if (store.isTimeAndMaterialsSelected) {
      store.generateInvoiceItemsForTimeAndMaterials(
        store.timeAndMaterialsStore.generateFrom,
      );
      store.timeAndMaterialsStore.setGenerateFrom(undefined);
    } else {
      await store.form.autocompleteInvoiceItemsFromSplits();
    }
  };

  const handleCreateNewTag = async (
    title: string,
    invoiceItem: TInvoiceItemForm,
  ) => {
    const tag = await tagStore.createTag(title);
    if (!tag) return;

    invoiceItem.$.tag_ids.onChange(
      invoiceItem.$.tag_ids.value?.concat(tag.id) ?? [tag.id],
    );
    return toTagOptions([tag])[0];
  };

  return (
    <Card sectioned>
      <Stack spacing="6">
        <Stack w="full">
          <HStack justifyContent="space-between" w="full">
            <Heading mb="3" fontWeight="semibold" size="sm">
              {store.type === InvoiceTypeEnum.CreditNote
                ? t`Text on credit note`
                : t`Text on invoice`}
            </Heading>
            <HStack spacing="2">
              <FormControl flexDir="row" display="flex" w="fit-content">
                <FormLabel htmlFor="show-account-classification">
                  <Trans>Show accounting details</Trans>
                </FormLabel>
                <Switch
                  id="show-account-classification"
                  isChecked={store.showAccountClassification.value}
                  onChange={store.showAccountClassification.toggle}
                />
              </FormControl>
              <FormControl flexDir="row" display="flex" w="fit-content">
                <FormLabel
                  color={
                    !store.showQuantity.value &&
                    store.form.invoice_items.$.some(
                      (item) => item.$.quantity.hasError,
                    )
                      ? "red.500"
                      : undefined
                  }
                  htmlFor="show-quantity"
                >
                  <Trans>Show quantity</Trans>
                </FormLabel>
                <Switch
                  id="show-quantity"
                  isChecked={store.showQuantity.value}
                  onChange={store.showQuantity.toggle}
                />
              </FormControl>
            </HStack>
          </HStack>
          <TextField
            type="text"
            placeholder={
              store.type === InvoiceTypeEnum.CreditNote
                ? t`Text on credit note`
                : t`Text on invoice`
            }
            {...fieldToInputProps(store.form.text_on_invoice)}
          />
        </Stack>

        <Stack ref={parent} spacing="3">
          <Grid
            alignItems="center"
            columnGap="3"
            templateColumns={templateColumns}
            color="gray.600"
            fontSize={{
              base: "xs",
              "2xl": "sm",
            }}
            fontWeight="bold"
          >
            <GridItem colSpan={3}>
              <HStack flex="1">
                <Heading fontWeight="semibold" size="sm">
                  {store.type === InvoiceTypeEnum.CreditNote
                    ? t`Credit note items`
                    : t`Invoice items`}
                </Heading>

                <IconButton
                  aria-label={t`Add invoice item`}
                  icon={
                    <Icon name="plus-circle" w="6" h="6" color="blue.500" />
                  }
                  onClick={createEmptyItem}
                  variant="plain"
                />
              </HStack>
            </GridItem>
            {store.showQuantity.value && (
              <>
                <GridItem>
                  <Trans>Unit price</Trans>
                </GridItem>
                <GridItem>
                  <Trans>Quantity</Trans>
                </GridItem>
              </>
            )}
            <GridItem>
              <Trans>Amount</Trans>
            </GridItem>
            <GridItem>
              <Trans>VAT</Trans>
            </GridItem>
          </Grid>
          {store.form.invoice_items.$.map((invoiceItem, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Items have no ID and don't change order so this is fine
            <Box key={index}>
              <Grid columnGap="3" templateColumns={templateColumns}>
                <GridItem colSpan={3}>
                  <TextField
                    type="text"
                    placeholder={t`Work description`}
                    {...fieldToInputProps(invoiceItem.$.description)}
                  />
                </GridItem>

                {store.showQuantity.value && (
                  <>
                    <GridItem>
                      <NumberInput
                        error={invoiceItem.$.unit_price.error}
                        currency
                        value={invoiceItem.$.unit_price.value}
                        onChange={(val) => {
                          invoiceItem.$.unit_price.onChange(val);

                          if (!val || val === "") return;
                          NumberModel.setFieldState(
                            invoiceItem.$.amount_in_currency,
                            Number(val) * Number(invoiceItem.$.quantity.$),
                          );
                        }}
                        suffix={store.selectedCurrency.code}
                        placeholder={t`Unit price`}
                      />
                    </GridItem>

                    <GridItem>
                      <NumberInput
                        value={invoiceItem.$.quantity.value}
                        onChange={(val) => {
                          invoiceItem.$.quantity.onChange(val);
                          if (!val || val === "") return;
                          NumberModel.setFieldState(
                            invoiceItem.$.amount_in_currency,
                            Number(val) * Number(invoiceItem.$.unit_price.$),
                          );
                        }}
                        placeholder={t`Quantity`}
                        error={invoiceItem.$.quantity.error}
                      />
                    </GridItem>
                  </>
                )}

                <GridItem>
                  <NumberInput
                    currency
                    placeholder={`${t`Amount in`} ${store.selectedCurrency.code ?? "-"}`}
                    suffix={store.selectedCurrency.code}
                    onBlur={() => {
                      const field = invoiceItem.$.amount_in_currency;
                      field.onChange(
                        field.$.setValue(field.$.value.toFixed(2)),
                      );
                    }}
                    {...fieldNumberModelToInputProps(
                      invoiceItem.$.amount_in_currency,
                      (value) => {
                        invoiceItem.$.unit_price.onChange(
                          (
                            Number(value) / Number(invoiceItem.$.quantity.$)
                          ).toString(),
                        );
                        store.convertAmountUsingAPI();
                      },
                    )}
                  />
                </GridItem>

                <GridItem>
                  <Select
                    size="sm"
                    value={invoiceItem.$.vat_percent_informative.value}
                    onChange={(val) => {
                      invoiceItem.$.vat_percent_informative.onChange(val);
                      store.form.syncGlobalVatFromItems();
                    }}
                    options={store.vatRatesOptions}
                  />
                </GridItem>

                <GridItem justifyContent="end">
                  <RemoveButton
                    onClick={() => store.form.removeInvoiceItem(index)}
                  />
                </GridItem>
              </Grid>

              {store.showAccountClassification.value && (
                <Grid
                  alignItems="center"
                  columnGap="3"
                  templateColumns={templateColumns}
                  mt={2}
                >
                  <GridItem>
                    <Text>
                      <strong>
                        <Trans>Accounting</Trans>
                      </strong>
                    </Text>
                  </GridItem>
                  <GridItem colSpan={store.showQuantity.value ? 2 : 1}>
                    <Select
                      size="sm"
                      placeholder={t`Select account classification`}
                      value={invoiceItem.$.accounting_account_id.value}
                      onChange={invoiceItem.$.accounting_account_id.onChange}
                      options={store.accountingAccountOptions}
                      error={invoiceItem.$.accounting_account_id.error}
                      menuMinWidth="150px"
                      isDisabled={cannot("user_accessAccountingAttributes")}
                    />
                  </GridItem>
                  <GridItem>
                    <Box isTruncated>
                      <Select
                        asPortal
                        size="sm"
                        placeholder={t`Select cost center`}
                        value={invoiceItem.$.cost_center_id.value}
                        onChange={invoiceItem.$.cost_center_id.onChange}
                        options={store.generateCostCenterOptions(
                          invoiceItem.$.cost_center_id.value,
                        )}
                        error={invoiceItem.$.cost_center_id.error}
                        menuMinWidth="150px"
                        isDisabled={cannot("user_accessAccountingAttributes")}
                      />
                    </Box>
                  </GridItem>
                  <GridItem colSpan={store.showQuantity.value ? 2 : 1}>
                    <TagSelect
                      size="sm"
                      value={invoiceItem.$.tag_ids.value}
                      onChange={invoiceItem.$.tag_ids.onChange}
                      error={invoiceItem.$.tag_ids.error}
                      options={store.tagOptions}
                      isLoading={store.appStore.tagStore.isLoading}
                      onCreateOption={async (title) =>
                        await handleCreateNewTag(title, invoiceItem)
                      }
                    />
                  </GridItem>
                  <GridItem />
                </Grid>
              )}
              {index < store.form.invoice_items.$.length - 1 && (
                <Divider pt="3" />
              )}
            </Box>
          ))}
          {store.isTimeAndMaterialsSelected &&
            !!lastInvoice &&
            lastInvoice.outgoingInvoice.invoice_no !== store.invoice_no && (
              <HStack>
                <Text>
                  <Trans>This project was already invoiced in</Trans>&nbsp;
                  <Link
                    href={{
                      pathname: lastInvoicePathname,
                      query: { id: lastInvoice.outgoingInvoice.id },
                    }}
                  >
                    #
                    {lastInvoice.outgoingInvoice?.invoice_no ??
                      lastInvoice.outgoingInvoice?.id}
                  </Link>
                  &nbsp;
                  <Trans>at</Trans>
                  &nbsp;
                  {formatDate(new Date(lastInvoice.outgoing_invoice_date))}.
                </Text>
              </HStack>
            )}
          {store.isBudgetItemsStepVisible && (
            <HStack justify="space-between">
              {store.isTimeAndMaterialsSelected ? (
                <Menu>
                  <MenuButton
                    as={Button}
                    mt="2"
                    rightIcon={<Icon name="chevron-down" />}
                    size="sm"
                    variant="subtle"
                  >
                    <Trans>Generate from</Trans>
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => {
                        store.timeAndMaterialsStore.setGenerateFrom(
                          "budget-item",
                        );
                        confirmAutocompleteInvoiceItemsModal.onOpen();
                      }}
                    >
                      <Trans>Budget items</Trans>
                    </MenuItem>
                    <Tooltip
                      isDisabled={
                        !store.timeAndMaterialsStore
                          .isGenerateFromGroupingDisabled
                      }
                      label={t`Option is disabled because a budget item amount was edited in previous step.`}
                    >
                      <MenuItem
                        isDisabled={
                          store.timeAndMaterialsStore
                            .isGenerateFromGroupingDisabled
                        }
                        onClick={() => {
                          store.timeAndMaterialsStore.setGenerateFrom(
                            "grouping",
                          );
                          confirmAutocompleteInvoiceItemsModal.onOpen();
                        }}
                      >
                        {store.timeAndMaterialsStore.selectedGroupByKey}
                      </MenuItem>
                    </Tooltip>
                  </MenuList>
                </Menu>
              ) : (
                <Button
                  mt="2"
                  leftIcon={<Icon name="refresh-cw-02" />}
                  onClick={confirmAutocompleteInvoiceItemsModal.onOpen}
                  size="sm"
                  variant="subtle"
                >
                  <Trans>Generate from budget items</Trans>
                </Button>
              )}
              <Button
                aria-label={t`Delete all items`}
                colorScheme="grey"
                onClick={store.form.removeAllInvoiceItems}
                rightIcon={<Icon name="trash-03" w="6" h="6" mr="2" ml="3" />}
                variant="link"
              >
                <Trans>Delete all</Trans>
              </Button>
            </HStack>
          )}
        </Stack>

        <Grid
          gap="2"
          templateColumns={{
            base: "1fr",
            md: "0.5fr 0.5fr",
          }}
        >
          <Box flex="1">
            <Text fontWeight="semibold" textTransform="uppercase">
              {store.type === InvoiceTypeEnum.CreditNote
                ? t`Credit note items total in credit note currency`
                : t`Invoice items total in invoice currency`}
            </Text>
          </Box>
          <Stack flex="1" spacing="4">
            {store.showVat && (
              <HStack justify="space-between">
                <HStack spacing="4">
                  <Text>
                    <Trans>Without VAT</Trans>
                  </Text>
                  <Text color="grey.500">
                    {store.form.invoice_items.$.length} <Trans>item/s</Trans>
                  </Text>
                </HStack>
                <Text>
                  {currency.formatByCurrency(
                    store.sumInvoiceItems,
                    store.selectedCurrency,
                  )}
                </Text>
              </HStack>
            )}

            {!!store.amountFromProofOfPayments && (
              <HStack justify="space-between">
                <Text>
                  <Trans>Used from proforma</Trans>
                </Text>
                <Text>
                  {currency.formatByCurrency(
                    store.amountFromProofOfPayments,
                    store.selectedCurrency,
                  )}
                </Text>
              </HStack>
            )}

            {store.showVat &&
              store.sumInvoiceItemsVatByPercent.map(
                ({ vatPercent, vatAmount, vatPercentFormatted }) => (
                  <HStack key={vatPercent} justify="space-between">
                    <Text>
                      <Trans>VAT</Trans>
                      &nbsp;({vatPercentFormatted})
                    </Text>
                    <Text>
                      {currency.formatByCurrency(
                        vatAmount,
                        store.selectedCurrency,
                      )}
                    </Text>
                  </HStack>
                ),
              )}

            {store.showVat && store.sumInvoiceItemsVatByPercent.length > 1 && (
              <HStack justify="space-between">
                <Text>
                  <Trans>Total VAT</Trans>
                </Text>
                <Text>
                  {currency.formatByCurrency(
                    store.sumInvoiceItemsVat,
                    store.selectedCurrency,
                  )}
                </Text>
              </HStack>
            )}

            <HStack justify="space-between">
              <Text>{totalText}</Text>
              <Text>
                {currency.formatByCurrency(
                  store.sumInvoiceItemsTotal,
                  store.selectedCurrency,
                )}
              </Text>
            </HStack>

            {!!store.amountFromProofOfPayments && (
              <HStack justify="space-between">
                <Text>
                  <Trans>Remaining amount with VAT</Trans>
                </Text>
                <Text>
                  {currency.formatByCurrency(
                    store.remaingingAmountWithVat,
                    store.selectedCurrency,
                  )}
                </Text>
              </HStack>
            )}

            {lithuanianWorkspace && (
              <HStack justify="space-between">
                <Text>
                  <Trans>Total in words</Trans>
                </Text>
                <Box w="70%">
                  <TextField
                    value={store.form.total_in_words.value ?? ""}
                    onChange={store.form.total_in_words.onChange}
                    connectedRight={
                      fetchingTotalInWords && <Spinner size="xs" />
                    }
                  />
                </Box>
              </HStack>
            )}

            {store.showInvoiceExchangeRate && (
              <>
                <HStack justify="space-between">
                  <Text>
                    <Trans>Invoice exchange rate</Trans>

                    <Tooltip
                      hasArrow
                      label={t`The rate shown on the invoice PDF. Also used when sending the invoice to accounting.`}
                      placement="top"
                      shouldWrapChildren
                    >
                      <Icon name="info-circle" boxSize="3" ml="1" />
                    </Tooltip>
                  </Text>
                  <Box w="50%">
                    <ExchangeRateInput
                      value={store.form.exchange_rate.value.value}
                      isLoading={store.exchangeRateFetcher.isLoading.value}
                      selectedSource={store.form.exchange_rate_source.value}
                      sourceCurrencyCode={
                        store.selectedLegalEntityCurrency?.code
                      }
                      onChange={store.form.handleExchangeRateChange}
                    />
                  </Box>
                </HStack>

                <HStack justify="space-between">
                  <Text>
                    <Trans>Invoice exchange rate source</Trans>
                  </Text>

                  <Box w="50%">
                    <ExchangeRateSourceSelect
                      value={store.form.exchange_rate_source.value}
                      onChange={store.form.handleChangeExchangeRateSource}
                      error={store.form.exchange_rate_source.error}
                    />
                  </Box>
                </HStack>
              </>
            )}

            {store.showWorkspaceExchangeRate && (
              <>
                <HStack justify="space-between">
                  <Text>
                    <Trans>Project exchange rate</Trans>

                    <Tooltip
                      hasArrow
                      label={t`The rate used to convert this invoice amount for project statistics and reporting.`}
                      placement="top"
                      shouldWrapChildren
                    >
                      <Icon name="info-circle" boxSize="3" ml="1" />
                    </Tooltip>
                  </Text>

                  <Box w="50%">
                    <ExchangeRateInput
                      value={store.form.workspace_exchange_rate.value.value}
                      isLoading={store.isWorkspaceExchangeRateLoading}
                      selectedSource={
                        store.form.workspace_exchange_rate_source.value
                      }
                      onChange={store.form.handleChangeWorkspaceExchangeRate}
                    />
                  </Box>
                </HStack>
                <HStack justify="space-between">
                  <Text>
                    <Trans>Project exchange rate source</Trans>
                  </Text>
                  <Box w="50%">
                    <ExchangeRateSourceSelect
                      value={store.form.workspace_exchange_rate_source.value}
                      onChange={
                        store.form.handleChangeWorkspaceExchangeRateSource
                      }
                      error={store.form.workspace_exchange_rate_source.error}
                    />
                  </Box>
                </HStack>
              </>
            )}

            {store.showExchangeRateUnsetWarning && (
              <Alert mb="4" rounded="lg" status="warning">
                <AlertIcon />
                <AlertDescription>
                  <Trans>
                    Conversion rate for selected currency is not set.
                  </Trans>
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </Grid>
      </Stack>
      <ModalConfirm
        isOpen={confirmAutocompleteInvoiceItemsModal.isOpen}
        heading={
          store.type === InvoiceTypeEnum.CreditNote
            ? t`Generate credit note items`
            : t`Generate invoice items`
        }
        message={
          store.type === InvoiceTypeEnum.CreditNote
            ? t`Credit note items will be generated from the budget items you selected. If you already filled some invoice items generated ones will replace those.`
            : t`Invoice items will be generated from the budget items you selected. If you already filled some invoice items generated ones will replace those.`
        }
        confirmBtnTitle={t`Generate`}
        cancelBtnTitle={t`Cancel`}
        onConfirm={onGenerateItems}
        onClose={() => {
          store.timeAndMaterialsStore.setGenerateFrom(undefined);
          confirmAutocompleteInvoiceItemsModal.onClose();
        }}
      />
    </Card>
  );
});
